import { formatDate, toDate } from "@nomnom/core";
import type { Payout, RiderProfile, User } from "@nomnom/core";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";

export type PayoutRow = Payout & { id: string };

export function formatTimestamp(
  value:
    | Payout["createdAt"]
    | Payout["paidAt"]
    | Payout["period"]["start"]
    | Payout["period"]["end"],
): string {
  return formatDate(value);
}

export function formatPeriod(period: Payout["period"]): string {
  if (!period?.start || !period?.end) {
    return "-";
  }

  const start = toDate(period.start);
  const end = toDate(period.end);
  if (!start || !end) {
    return "-";
  }

  return `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`;
}

export async function loadPayouts(): Promise<{
  payouts: PayoutRow[];
  riderProfiles: Record<string, RiderProfile>;
  users: Record<string, User>;
}> {
  try {
    const payoutsSnap = await getDocs(
      query(collection(db, "payouts"), orderBy("createdAt", "desc")),
    );
    const payouts = payoutsSnap.docs.map((docSnap) => ({
      ...(docSnap.data() as Payout),
      id: docSnap.id,
    }));

    const riderIds = Array.from(
      new Set(payouts.map((payout) => payout.riderUid)),
    );
    const riderProfiles: Record<string, RiderProfile> = {};
    const users: Record<string, User> = {};

    for (const uid of riderIds) {
      const profileSnap = await getDoc(doc(db, "riderProfiles", uid));
      if (profileSnap.exists()) {
        riderProfiles[uid] = profileSnap.data() as RiderProfile;
      }

      const userSnap = await getDoc(doc(db, "users", uid));
      if (userSnap.exists()) {
        users[uid] = {
          uid: userSnap.id,
          ...(userSnap.data() as Omit<User, "uid">),
        };
      }
    }

    return { payouts, riderProfiles, users };
  } catch (error) {
    console.error("[PayoutsService] Failed to load payouts:", error);
    throw new Error("Failed to load payouts");
  }
}

export async function markPayoutPaid(
  payoutId: string,
  paidMethod: string,
  fallbackMethod: string | undefined,
  paidNotes: string,
  adminUid?: string | null,
): Promise<void> {
  try {
    await updateDoc(doc(db, "payouts", payoutId), {
      status: "paid",
      paidAt: Timestamp.now(),
      failedAt: null,
      failureReason: null,
      paidBy: adminUid ?? null,
      paymentMethod: paidMethod || fallbackMethod || "manual_mercury_ach",
      notes: paidNotes.trim() || null,
    });
  } catch (error) {
    console.error("[PayoutsService] Failed to mark paid:", error);
    throw new Error("Failed to update payout");
  }
}

export async function markPayoutFailed(
  payoutId: string,
  adminUid?: string | null,
): Promise<void> {
  try {
    await updateDoc(doc(db, "payouts", payoutId), {
      status: "failed",
      failedAt: Timestamp.now(),
      paidAt: null,
      paidBy: adminUid ?? null,
      paymentMethod: "manual_mercury_ach",
      failureReason: "Manual Mercury ACH payout needs attention.",
    });
  } catch (error) {
    console.error("[PayoutsService] Failed to mark failed:", error);
    throw new Error("Failed to update payout");
  }
}
